import React, { useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import TextView from "../../../components/TextView";
import Button from "../../../components/Button";
import { colors } from "../../../utils";
import styled from "styled-components";
import icon from "assets/images/download-report.svg";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MenuItem from "@mui/material/MenuItem";
import { downloadReferralsReport } from "../../../services/api";
import { toast } from "react-toastify";

const Container = styled.div`
  position: relative;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  min-width: 400px;
  width: 50vw;
  min-width: 550px;
  background-color: ${colors.white};
  border-radius: 10px;
  padding: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const ButtonView = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const Icon = styled.img`
  width: 50px;
  height: 50px;
  margin-bottom: 20px;
`;

const SelectWrapper = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${colors.lightGrey};
  border-radius: 5px;
  padding: 8px;
  margin-top: 20px;
`;

const CustomSelect = styled.div`
  cursor: pointer;
  display: flex;
`;

const CustomMenuItem = styled(MenuItem)`
  .MuiListItem-root {
    padding: 8px;
  }
`;

const CustomDropdown = styled.div`
  position: absolute;
  top: 30px;
  background-color: ${colors.background};
  border-radius: 5px;
  width: 80px;
  z-index: 12;
  max-height: 160px;
  overflow-y: scroll;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${colors.background};
    border-radius: 5px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.lightGrey};
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #888;
  }
`;

const Separator = styled.div`
  width: 100%;
  margin: 24px;
  height: 1px;
  background-color: ${colors.lightBorder};
`;

const Outer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  z-index: 10;
`;

const months = {
  0: "All",
  1: "Jan",
  2: "Feb",
  3: "Mar",
  4: "Apr",
  5: "May",
  6: "Jun",
  7: "Jul",
  8: "Aug",
  9: "Sep",
  10: "Oct",
  11: "Nov",
  12: "Dec",
};

export default function ReportPopup({ open, onClose }) {
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [years, setYears] = useState([]);
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(
    currentDate.getMonth() + 1
  );

  useEffect(() => {
    generateYearOptions();
  }, []);

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    // Don't go earlier than 2025
    const minYear = Math.max(2025, currentYear - 10);
    for (let i = currentYear; i >= minYear; i--) {
      years.push(i);
    }
    setYears(years);
  };

  const onHideThings = () => {
    setShowYearDropdown(false);
    setShowMonthDropdown(false);
  };

  const onChangeHandler = (type, option) => {
    if (type === "year") {
      setSelectedYear(option);
      setShowYearDropdown(false);
    } else {
      setSelectedMonth(parseInt(option));
      setShowMonthDropdown(false);
    }
  };

  const onDownloadReport = async () => {
    if (!selectedYear) return toast.error("Please select a year");

    setLoading(true);

    const timeFrame = selectedMonth
      ? `?year=${selectedYear}&month=${
          selectedMonth === 0 ? "" : selectedMonth
        }`
      : `?year=${selectedYear}`;
    downloadReferralsReport(timeFrame)
      .then((res) => {
        window.open(res?.data?.url, "_blank");
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Container>
        <Icon src={icon} />
        <TextView
          variant="title"
          style={{ textAlign: "center", marginBottom: 20 }}
        >
          Download Your Report
        </TextView>
        <TextView variant="subtitle" style={{ textAlign: "center" }}>
          Get a detailed overview of your data in just a click.
        </TextView>
        <TextView variant="subtitle" style={{ textAlign: "center" }}>
          Select the date range for your report.
        </TextView>

        <div>
          <SelectWrapper>
            <CalendarTodayIcon
              style={{ color: colors.lightGrey, marginRight: 8 }}
            />{" "}
            <div style={{ position: "relative" }}>
              <CustomSelect onClick={() => setShowYearDropdown(true)}>
                {selectedYear ? selectedYear : "Year"}
                <KeyboardArrowDownIcon style={{ color: colors.lightGrey }} />
              </CustomSelect>
              {showYearDropdown && (
                <CustomDropdown>
                  {years.map((option) => (
                    <CustomMenuItem
                      onClick={() => onChangeHandler("year", option)}
                      key={option}
                    >
                      {option}
                    </CustomMenuItem>
                  ))}
                </CustomDropdown>
              )}
            </div>
            <div style={{ position: "relative" }}>
              <CustomSelect
                style={{ marginLeft: 10 }}
                onClick={() => setShowMonthDropdown(true)}
              >
                {selectedMonth !== null ? months[selectedMonth] : "Month"}
                <KeyboardArrowDownIcon style={{ color: colors.lightGrey }} />
              </CustomSelect>
              {showMonthDropdown && (
                <CustomDropdown>
                  {Object.keys(months).map((key) => (
                    <CustomMenuItem
                      onClick={() => onChangeHandler("month", key)}
                      key={key}
                    >
                      {months[key]}
                    </CustomMenuItem>
                  ))}
                </CustomDropdown>
              )}
            </div>
          </SelectWrapper>
        </div>

        {(showYearDropdown || showMonthDropdown) && (
          <Outer onClick={onHideThings} />
        )}
        <Separator />
        <ButtonView>
          <Button
            variant={"secondary"}
            onClick={onClose}
            style={{
              width: 120,
              marginRight: 20,
            }}
          >
            Not now
          </Button>
          <Button
            isLoading={loading}
            style={{
              width: 140,
            }}
            onClick={onDownloadReport}
          >
            Download
          </Button>
        </ButtonView>
      </Container>
    </Modal>
  );
}
